import { months } from 'moment';
import * as moment from 'moment-timezone';
import { localStorageKeys } from '../common/constants/local.storage.keys';
import { timeConfig } from '../common/constants/time.config';
import { clone } from '../common/extensions';
import { DefaultImageWebSideService } from '../common/services/default.images/default.image.web.side.service';
import { HubEvent } from '../models/hub/hub.event';

export class EventTimeService {
  public static toPDFTimeFormat(original: Date): any {
    let date = moment(original);
    return date.tz(timeConfig.timezone).format(timeConfig.pdfTimeFormat);
  }

  public static toPDFTimeBoxFormat(original: Date): PDFTimeBox {
    let date = moment(original);
    let month = date.tz(timeConfig.timezone).format('MMM').toUpperCase();
    let date1 = date.tz(timeConfig.timezone).format('D').toString();
    let day = date.tz(timeConfig.timezone).format('ddd').toString();
    let time = date.tz(timeConfig.timezone).format('LT').toString();

    return new PDFTimeBox(month, date1, day, time);
  }

  public static formatEventTime(events: HubEvent[]) {
    if (events && events.length) {
      events.forEach((x) => {
        EventTimeService.convertTime(x);
        if (x.eventTimes && x.eventTimes.length) {
          x.eventTimes.forEach((y) => {
            EventTimeService.convertTime(y);
          });
          x.eventTimes.sort((a, b) => {
            return a.startTime > b.startTime ? 1 : -1;
          });
        }
      });
    }
  }

  public static convert(x: Date | string) {
    let date = moment(x);
    return date.tz(timeConfig.timezone).format(timeConfig.timeFormat);
  }

  public static convertByTimezonFormatter(
    x: Date | string,
    formatter: string,
    isReadableFormat = true
  ) {
    let date = moment(x);
    formatter = formatter || this.getTimezoneFormatter();
    return date
      .tz(formatter)
      .format(isReadableFormat ? timeConfig.timeFormat : timeConfig.standardFormat);
  }

  public static convertByTimezoneFormatterIncludeNoTimeCheck(
    x: Date | string,
    formatter: string,
    isReadableFormat = true
  ) {
    if (!x) return '';
    let date = moment(x);
    formatter = formatter || this.getTimezoneFormatter();
    let isNoTime = EventTimeService.isNoTime(date, formatter);

    return date
      .tz(formatter)
      .format(
        isNoTime
          ? timeConfig.pdfTimeFormat
          : isReadableFormat
          ? timeConfig.timeFormat
          : timeConfig.standardFormat
      );
  }

  public static getTime(x: string) {
    if (x.indexOf(' ') === -1 && x.indexOf('T') >= 0 && x.indexOf('Z') >= 0) {
      x = x.replace(/(T)+/g, ' ');
      x = x.replace(/(Z)+/g, ' ');
    }
    let seg = x.split(' ');
    let timeSeg = seg[1].split(':');
    // timeSeg[0] = timeSeg[0].startsWith('0') ? timeSeg[0].substr(1) : timeSeg[0];
    // timeSeg[1] = timeSeg[1] === '00' ? '0' : timeSeg[1];
    return `${timeSeg[0]}:${timeSeg[1]}`;
  }

  public static convertToDateByTimezonFormatter(x: Date | string, formatter: string) {
    let date = moment(x);
    formatter = formatter || this.getTimezoneFormatter();
    return date.tz(formatter);
  }

  public static getLocalHourByFormatter(x: Date | string, formatter: string) {
    const hour = this.convertToDateByTimezonFormatter(x, formatter).format('HH');
    return parseInt(hour, 10);
  }

  public static convertToDateByTimezonCode(x: string, timezone: string) {
    let dateString = new Date(x).toISOString();
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    let date = moment(dateString).tz(formatter).format(timeConfig.standardFormat);
    //dateString = dateString.replace(/(T)+/g, " ");
    //dateString = dateString.replace(/(Z)+/g, " ");
    return `${date} ${timezone}`;
  }

  public static convertToDateTimeByTimezonCode(x: string, timezone: string) {
    let dateString = new Date(x).toISOString();
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    // let date = moment(dateString).tz(formatter).format(timeConfig.dateTimeFormatWithoutTimezone);
    let date = new Date(`${dateString} ${timezone}`);
    return date;
  }

  public static getYear(x: string, timezoneFormatter: string) {
    if (!x) return new Date().getFullYear();

    try {
      let dateString = new Date(x).toISOString();
      let date = moment(dateString)
        .tz(timezoneFormatter)
        .format(timeConfig.dateTimeFormatWithoutTimezone);
      const year = new Date(date).getFullYear();
      if (isNaN(year)) {
        console.log('year NaN, init: ', x);
        console.log('year NaN, timezoneFormatter: ', timezoneFormatter);
        console.log('year NaN, date: ', date);
        return new Date().getFullYear();
      }
      return year;
    } catch (e) {
      console.log(e);
      return new Date().getFullYear();
    }
  }

  public static addHours(date: string | Date, hours: number) {
    // let t = new Date(date);
    // let t_s = t.getTime();
    //
    // t.setTime(t_s + 1000 * 60 * 60 * hours);
    // return t.toISOString();
    let s = moment(date).add(hours, 'hours');
    return s.toISOString();
  }

  public static addUTCHours(date: string, hours: number) {
    let t = new Date(date);
    let t_s = t.getTime();

    t.setTime(t_s + 1000 * 60 * 60 * hours);
    return t.toISOString();
  }

  public static diff(start: string | Date, end: string | Date, type = 'days') {
    let a = moment(start);
    let b = moment(end);

    return type !== 'biweekly' ? b.diff(a, type as any) : Math.floor(b.diff(a, 'weeks') / 2);
  }

  public static getDefaultStartTime(timezone: string) {
    let timezoneCode = timezone || this.getTimezoneCode();
    // let date = new Date(`${new Date().toLocaleDateString()} 19:00 ${timezoneCode}`).toISOString();
    let date = new Date(`${moment().format('L')} 19:00 ${timezoneCode}`).toISOString();
    return date;
  }

  public static toDayAndMonthFormat(x: Date) {
    let date = moment(x);
    return date.format(timeConfig.dayAndMonth).toString();
  }

  public static getDayAndMonth(date: string | Date, timezone: string) {
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    return moment(date).tz(formatter).format(timeConfig.dayAndMonth).toString();
  }

  public static getDayAndMonthAndYear(date: string | Date, timezone: string) {
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    return moment(date).tz(formatter).format(timeConfig.pdfTimeFormat).toString();
  }

  public static getDayOfWeek(date: string | Date, timezone: string) {
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    return moment(date).tz(formatter).day();
  }

  public static getWeekDayNo(date: any, timezone: string) {
    let previousWeek = clone(date);
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    let no = 0;
    let isLast = false;
    while (moment(date).tz(formatter).isSame(previousWeek, 'month')) {
      previousWeek = moment(previousWeek).tz(formatter).add(-1, 'weeks');
      no++;
    }
    let nextWeek = moment(date).tz(formatter).add(1, 'weeks');
    if (!moment(date).tz(formatter).isSame(nextWeek, 'month')) {
      isLast = true;
    }
    return {
      no,
      isLast,
      text: ['first', 'second', 'third', 'fourth', 'last'][no - 1],
    };
  }

  public static getSameDayInWeekOfMonth(
    start: string,
    dayOfWeek: number,
    no: number,
    timezone: string
  ) {
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);

    let startUTCHours = new Date(start).getUTCHours();
    let dayInNextMonth = moment(start).tz(formatter).date(1);

    // use ISO time to save, and show based on Daylight or Startard time when displaying. See adjustWithTimeSaving function
    // if (new Date(dayInNextMonth).getUTCHours() !== startUTCHours) {
    if (moment(dayInNextMonth).utc().hours() !== startUTCHours) {
      // dayInNextMonth = new Date(dayInNextMonth.toISOString()).setUTCHours(startUTCHours);
      dayInNextMonth = moment(dayInNextMonth).utc().hours(startUTCHours);
    }
    let meetNo = 1;
    while (this.getDayOfWeek(dayInNextMonth, timezone) !== dayOfWeek || meetNo !== no) {
      if (this.getDayOfWeek(dayInNextMonth, timezone) === dayOfWeek) {
        dayInNextMonth = this.addUTCHours(
          moment(dayInNextMonth).toISOString(),
          24 * 7 * (no - meetNo)
        );
        break;
      }
      dayInNextMonth = this.addUTCHours(moment(dayInNextMonth).toISOString(), 24);
    }
    return moment(dayInNextMonth).toISOString();
  }

  public static getSameDayInWeek(start, end, dayOfWeek, no, timezone) {
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    let startUTCHours = new Date(start).getUTCHours();
    let days = [];
    while (moment(start).tz(formatter).isBefore(moment(end).tz(formatter))) {
      let dayInMonth = this.getSameDayInWeekOfMonth(start, dayOfWeek, no, timezone);
      console.log(`dayInMonth = ${dayInMonth}`);
      if (moment(dayInMonth).tz(formatter).isBefore(moment(end).tz(formatter))) {
        days.push(dayInMonth);
        start = moment(dayInMonth).tz(formatter).add(1, 'months').date(1);
        // use ISO time to save, and show based on Daylight or Startard time when displaying. See adjustWithTimeSaving function
        if (new Date(start.toISOString()).getUTCHours() !== startUTCHours) {
          start = new Date(start.toISOString()).setUTCHours(startUTCHours);
        }
      } else {
        break;
      }
    }
    console.log(days);
    return days;
  }

  public static getLastWeekDays(start, end, dayOfWeek, timezone) {
    timezone = timezone || this.getTimezoneCode();
    let formatter = this.getTimezoneFormatterByTimezone(timezone);
    let startUTCHours = new Date(start).getUTCHours();
    let days = [];

    while (moment(start).tz(formatter).isBefore(moment(end).tz(formatter))) {
      let dayOfMonth = moment(start).tz(formatter).add(1, 'months').date(1).add(-1, 'days');
      if (new Date(dayOfMonth).getUTCHours() !== startUTCHours) {
        dayOfMonth = new Date(dayOfMonth.toISOString()).setUTCHours(startUTCHours);
      }

      while (this.getDayOfWeek(dayOfMonth, timezone) !== dayOfWeek) {
        dayOfMonth = moment(dayOfMonth).add(-1, 'days');
        // use ISO time to save, and show based on Daylight or Startard time when displaying. See adjustWithTimeSaving function
        if (new Date(dayOfMonth).getUTCHours() !== startUTCHours) {
          dayOfMonth = new Date(dayOfMonth.toISOString()).setUTCHours(startUTCHours);
        }
      }
      if (moment(dayOfMonth).tz(formatter).isBefore(moment(end).tz(formatter))) {
        days.push(moment(dayOfMonth).toISOString());
      } else {
        break;
      }
      start = moment(start).tz(formatter).add(1, 'months').date(1);
    }
    console.log(days);
    return days;
  }

  public static convertByGuessTimezoneFormatter(x: Date | string) {
    let date = moment(x);
    let formatter = EventTimeService.getTimezoneFormatter();
    return date.tz(formatter).format(timeConfig.timeFormat);
  }

  public static toLocalDate(x: Date | string, formatter: string) {
    let date = moment(x);
    formatter = formatter || this.getTimezoneFormatter();
    return date.tz(formatter).format(timeConfig.pdfTimeFormat);
  }

  public static toLocalDateTime(x: Date | string, formatter: string) {
    let date = moment(x);
    formatter = formatter || this.getTimezoneFormatter();
    return date.tz(formatter).format(timeConfig.dateTimeFormat);
  }

  public static toShortDateFormat(x: Date) {
    let date = moment(x);
    return date.format(timeConfig.pdfTimeFormat).toString();
  }

  public static toDateFormat(x: Date) {
    let date = moment(x);
    return date.format(timeConfig.dateFormat).toString();
  }

  public static toNonTimezoneTimeText(x: Date) {
    let date = moment(x);
    return date.format(timeConfig.nonTimeZoneTimeFormat).toString();
  }

  public static getTimezoneCode() {
    let zone = moment.tz.guess(); // "America/Los_Angeles"
    return moment.tz(zone).format('z'); // either "PST" or "PDT", depending on when run
  }

  public static getTimezoneFormatter() {
    return moment.tz.guess(); // "America/Los_Angeles"
  }

  public static getTimezoneCodeByFormatter(formatter: string) {
    return moment.tz(formatter).format('z');
  }

  public static getTimezonecodeWithTimeSaving(x: Date | string, formatter: string) {
    let date = moment(x);
    formatter = formatter || this.getTimezoneFormatter();
    return date.tz(formatter).format('z');
  }

  public static getDateFromTimeText(x: string) {
    if (!x) return '';
    if (x.indexOf(' @ ') >= 0) {
      let seg = x.split(' @ ');
      return seg[0];
    }
    return '';
  }

  public static getDateFromTime(x: Date | string) {
    let date = this.convert(x);
    return this.getDateFromTimeText(date);
  }

  public static getTimeFromTimeText(x: string, timezone?: string) {
    if (!x) return '';
    if (x.indexOf(' @ ') >= 0) {
      let seg = x.split(' @ ');
      return seg[1] + (timezone ? ' ' + timezone : '');
    }
    return '';
  }

  public static getTimeFromTime(x: Date | string) {
    let date = this.convert(x);
    return this.getTimeFromTimeText(date);
  }

  public static convertToTravelTimeText(totalTravelTime: number) {
    return totalTravelTime / 60 > 1
      ? Math.floor(totalTravelTime / 60) + 'hrs ' + Math.round(totalTravelTime % 60) + 'min'
      : totalTravelTime + 'min';
  }

  public static isNoTimeEvent(event) {
    //if (event.socialName === 'eventful') {
    // let startTime = event.isManuallyCreated ? EventTimeService.adjustWithTimeSaving(event.startTime, event.timezoneFormatter, event.timezone) : event.startTime;
    let startTime = event.startTime;
    let date = EventTimeService.toLocalDateTime(startTime, event.timezoneFormatter);
    if (date.indexOf(' ') >= 0) {
      let seg = date.split(' ');
      if (seg.length >= 2 && seg[1] == '00:00:00') {
        return true;
      }
    }
    return false;
  }

  public static isNoTime(time, timezoneFormatter) {
    let date = EventTimeService.toLocalDateTime(time, timezoneFormatter);
    if (date.indexOf(' ') >= 0) {
      let seg = date.split(' ');
      if (seg.length >= 2 && seg[1] == '00:00:00') {
        return true;
      }
    }
    return false;
  }

  public static processEventTime(item: HubEvent, useDefaultTimezoneFormatter = true) {
    let timezoneFormatterDefault = localStorage.getItem(localStorageKeys.timezoneFormatterDefault);
    timezoneFormatterDefault =
      timezoneFormatterDefault === 'undefined' ? undefined : timezoneFormatterDefault;
    if (useDefaultTimezoneFormatter && timezoneFormatterDefault && !item.isManuallyCreated) {
      item.timezoneFormatter = timezoneFormatterDefault;
      // let timezone = moment().tz(timezoneFormatterDefault).format('zz');
      // item.timezone = timezone;
    }
    if (!item.timezoneFormatter) {
      item.timezoneFormatter = EventTimeService.getTimezoneFormatterByTimezone(item.timezone);
    }
    let today = new Date();
    let newEventTimes = [];
    this.setTimezone(item, timezoneFormatterDefault);

    if (!item.eventTimes || !item.eventTimes.length) {
      item.eventTimes = [{ startTime: item.startTime, endTime: item.endTime }];
    }
    item.eventTimes.sort((a, b) => {
      return a.startTime > b.startTime ? 1 : -1;
    });
    if (item.eventTimes && item.eventTimes.length) {
      item.eventTimesText = [];
      item.eventTimes.forEach((x, index) => {
        let startDateTime = new Date(x.startTime);
        let endDateTime = new Date(x.endTime);
        if (endDateTime >= today || (!x.endTime && startDateTime >= today)) {
          this.setStartTimeFormatted(
            x,
            item.timezoneFormatter,
            item.timezone,
            item.isManuallyCreated
          );
          this.setEndTimeFormatted(
            x,
            item.timezoneFormatter,
            item.timezone,
            item.isManuallyCreated
          );
          newEventTimes.push(x);
          item.eventTimesText.push({ startTimeText: x.startTimeText, endTimeText: x.endTimeText });
        }
      });

      if (newEventTimes.length) {
        item.eventTimes = newEventTimes;
        item.startTime = newEventTimes[0].startTime;
        item.endTime = newEventTimes[newEventTimes.length - 1].endTime;
        item.startTimeFormatted = newEventTimes[0].startTimeFormatted;
        item.endTimeFormatted = newEventTimes[newEventTimes.length - 1].endTimeFormatted;
      } else {
        item.eventTimes = [];
      }
    }
    this.setStartTimeFormatted(item, item.timezoneFormatter, item.timezone, item.isManuallyCreated);
    this.setEndTimeFormatted(item, item.timezoneFormatter, item.timezone, item.isManuallyCreated);

    if (EventTimeService.isNoTimeEvent(item)) {
      this.setNoTimeEvent(item);
    }

    this.setStartEndTimeCombination(item);
    item = DefaultImageWebSideService.getDefaultImageByKeyword(item);

    // clients don't know what does EST and EDT stands for, they only know EST, stupid...
    //this.showOnlyStandardTime(item);
    return item;
  }

  public static setNoTimeEvent(item) {
    item.isNoTime = true;
    item.startTimeFormatted = EventTimeService.toShortDateFormat(item.startTime);
    if (item.endTime) {
      let endDate = new Date(EventTimeService.toLocalDate(item.endTime, item.timezoneFormatter));
      item.endTimeFormatted = EventTimeService.toShortDateFormat(endDate);
    }
    if (item.eventTimes && item.eventTimes.length) {
      item.eventTimesText = [];
      item.eventTimes.forEach((x) => {
        x.startTimeFormatted = EventTimeService.toShortDateFormat(x.startTime);
        if (x.endTime) {
          let endDate = new Date(EventTimeService.toLocalDate(x.endTime, item.timezoneFormatter));
          x.endTimeFormatted = EventTimeService.toShortDateFormat(endDate);
        }
        item.eventTimesText.push({ startTimeText: x.startTimeText, endTimeText: x.endTimeText });
      });
    }
  }

  private static convertTime(x: HubEvent) {
    if (x.startTime && !x.startTimeFormatted) {
      let date = moment(new Date(x.startTime));
      x.startTimeFormatted = date.tz(timeConfig.timezone).format(timeConfig.timeFormat);
    }
    if (x.endTime && !x.endTimeFormatted) {
      let date = moment(new Date(x.endTime));
      x.endTimeFormatted = date.tz(timeConfig.timezone).format(timeConfig.timeFormat);
    }
  }

  public static adjustWithTimeSaving(time, formatter, timezoneCodeWhenAdded) {
    let timezone = EventTimeService.getTimezonecodeWithTimeSaving(time, formatter);
    let newTime = time;
    if (timezoneCodeWhenAdded && timezone !== timezoneCodeWhenAdded) {
      if (timezone.indexOf('S') >= 0) {
        newTime = EventTimeService.addUTCHours(time, 1);
      } else if (timezone.indexOf('D') >= 0) {
        newTime = EventTimeService.addUTCHours(time, -1);
      }
    }
    return newTime;
  }

  public static setStartTimeFormatted(item, formatter?, timezoneCode?, isManualCreated = false) {
    if (item.startTime) {
      if (formatter || item.timezoneFormatter) {
        // let newStartTime = isManualCreated ? EventTimeService.adjustWithTimeSaving(item.startTime, formatter || item.timezoneFormatter, timezoneCode) : null;
        let newStartTime = null;
        item.startTimeFormatted = EventTimeService.convertByTimezonFormatter(
          newStartTime || item.startTime,
          formatter || item.timezoneFormatter
        );
      } else if (timezoneCode || item.timezone) {
        let timeFormatter = EventTimeService.getTimezoneFormatterByTimezone(
          timezoneCode || item.timezone
        );
        item.startTimeFormatted = EventTimeService.convertByTimezonFormatter(
          item.startTime,
          timeFormatter
        );
      } else {
        item.startTimeFormatted = EventTimeService.convertByGuessTimezoneFormatter(
          new Date(item.startTime)
        );
      }
      item.startTimeText = item.startTimeFormatted.substr(
        0,
        item.startTimeFormatted.lastIndexOf(' ') + 1
      );
    }
  }

  public static setEndTimeFormatted(item, formatter?, timezoneCode?, isManualCreated = false) {
    if (item.endTime) {
      if (formatter || item.timezoneFormatter) {
        // let newEndTIme = isManualCreated ? EventTimeService.adjustWithTimeSaving(item.endTime, formatter || item.timezoneFormatter, timezoneCode) : null;
        let newEndTIme = null;
        item.endTimeFormatted = EventTimeService.convertByTimezonFormatter(
          newEndTIme || item.endTime,
          formatter || item.timezoneFormatter
        );
      } else if (timezoneCode || item.timezone) {
        let timeFormatter = EventTimeService.getTimezoneFormatterByTimezone(
          timezoneCode || item.timezone
        );
        item.endTimeFormatted = EventTimeService.convertByTimezonFormatter(
          item.endTime,
          timeFormatter
        );
      } else {
        item.endTimeFormatted = EventTimeService.convertByGuessTimezoneFormatter(
          new Date(item.endTime)
        );
      }
      item.endTimeText = item.endTimeFormatted.substr(
        0,
        item.endTimeFormatted.lastIndexOf(' ') + 1
      );

      if (item.endTimeFormatted && (item.timezoneFormatter || formatter)) {
        const startYear = EventTimeService.getYear(
          item.startTime,
          item.timezoneFormatter || formatter
        );
        const endYear = EventTimeService.getYear(item.endTime, item.timezoneFormatter || formatter);
        if (startYear !== endYear) {
          let seg = item.endTimeFormatted.split(' @ ');
          seg[0] += ', ' + endYear;
          item.endTimeFormatted = seg.join(' @ ');
        }
      }
    }
  }

  private static setTimezone(item, timezoneFormatterDefault?) {
    // some wrong manual events have timezone UTC
    item.timezone = item.timezone === 'UTC' ? null : item.timezone;

    if (!item.timezone && !item.timezoneFormatter && timezoneFormatterDefault) {
      item.timezoneFormatter = timezoneFormatterDefault;
    }
    if (item.timezone && !item.timezoneFormatter) {
      item.timezoneFormatter = EventTimeService.getTimezoneFormatterByTimezone(item.timezone);
    }
    if (!item.timezone && item.timezoneFormatter) {
      item.timezone = EventTimeService.getTimezoneCodeByFormatter(item.timezoneFormatter);
    }
    if (!item.isManuallyCreated) {
      item.timezone = EventTimeService.getTimezonecodeWithTimeSaving(
        item.startTime,
        item.timezoneFormatter
      );
    }
  }

  private static setStartEndTimeCombination(item: HubEvent) {
    if (!item.startTime) return;

    if (!item.eventTimes.length) {
      item.eventTimes.push({
        startTime: item.startTime,
        endTime: item.endTime,
        startTimeFormatted: item.startTimeFormatted,
        endTimeFormatted: item.endTimeFormatted,
        noTimeFormatted: null,
        timeFormatted: null,
      });
    }
    item.eventTimes.forEach((x) => {
      let startTimeSeg = x.startTimeFormatted.split(' @ ');
      let startDate = startTimeSeg[0].trim();
      let startDateSeg = startTimeSeg[0].split(' ');
      x.year = EventTimeService.getYear(x.startTime, item.timezoneFormatter);
      x.endYear = EventTimeService.getYear(x.endTime, item.timezoneFormatter);
      x.month = startDateSeg[0];
      x.day = startDateSeg[1];
      x.start = startTimeSeg[1];
      let endTimeSeg = null,
        endDate = null;
      if (x.endTimeFormatted) {
        endTimeSeg = x.endTimeFormatted.split('@');
        x.end = endTimeSeg[1] ? endTimeSeg[1].trim() : null;
        endDate = endTimeSeg[0].trim();
      }

      x.weekDay = EventTimeService.getWeekDayText(x.startTime, item.timezoneFormatter).substr(0, 3);
      x.endWeekDay = EventTimeService.getWeekDayText(x.endTime, item.timezoneFormatter);
      x.endWeekDay = x.endWeekDay ? x.endWeekDay.substr(0, 3) : null;
      let endSeg = x.endTimeFormatted ? x.endTimeFormatted.split(' ') : null;

      if (!x.start) {
        if (x.startTimeFormatted === x.endTimeFormatted) {
          x.noTimeFormatted = `${x.month} ${x.day}, ${x.year}`;
        } else {
          if (x.year === x.endYear) {
            let endString = endSeg
              ? ` - ${x.month === endSeg[0] ? '' : endSeg[0]} ${endSeg[1]}, ${x.year}`
              : '';
            x.noTimeFormatted = `${x.month} ${x.day} ${endString || x.year}`;
          } else {
            let endString = endSeg
              ? ` - ${x.month === endSeg[0] ? '' : endSeg[0]} ${endSeg[1]}, ${endSeg[2]}`
              : '';
            x.noTimeFormatted = `${x.month} ${x.day}, ${x.year} ${endString}`;
          }
        }
        x.timeFormatted = x.weekDay;
      } else {
        x.timeFormatted = `${x.weekDay} at ${x.start} ${x.end ? 'to ' + x.end : ''}`;
        let endString = endSeg ? ` - ${endSeg[0]} ${endSeg[1]}` : '';
        x.noTimeFormatted = `${x.month} ${x.day}, ${x.year} ${endString}`;
        if (x.year === x.endYear) {
          if (startDate === endDate) {
            x.noTimeFormatted = `${x.month} ${x.day}, ${x.year}`;
          } else {
            x.noTimeFormatted = `${x.month} ${x.day} ${endString}, ${x.year}`;
          }
        } else {
          x.noTimeFormatted = `${x.month} ${x.day}, ${x.year} ${
            endString ? endString + ', ' + x.endYear : ''
          }`;
        }
      }
    });

    item['noTimeFormatted'] = item.eventTimes[0].noTimeFormatted;
    item['timeFormatted'] = item.eventTimes[0].timeFormatted;
  }

  public static getWeekDayText(date, timezoneFormatter) {
    if (!date) return null;
    date = moment(date);
    return date.tz(timezoneFormatter).format('dddd');
  }

  public static getTimezoneFormatterByTimezone(timezone: string) {
    if (!timezone) return 'America/New_York';

    let index = ['EDT', 'EST'].getElementIndex((x) => x === timezone.toUpperCase());
    if (index !== -1) {
      return 'America/New_York';
    }

    let index1 = ['PDT', 'PST'].getElementIndex((x) => x === timezone.toUpperCase());
    if (index1 !== -1) {
      return 'America/Los_Angeles';
    }

    let index2 = ['CST', 'CDT'].getElementIndex((x) => x === timezone.toUpperCase());
    if (index2 !== -1) {
      return 'America/Menominee';
    }

    let index3 = ['MST', 'MDT'].getElementIndex((x) => x === timezone.toUpperCase());
    if (index3 !== -1) {
      return 'America/Denver';
    }

    let index4 = ['AKST', 'AKDT'].getElementIndex((x) => x === timezone.toUpperCase());
    if (index4 !== -1) {
      return 'America/Nome';
    }

    let index5 = ['CEST', 'CEDT', 'CET'].getElementIndex((x) => x === timezone.toUpperCase());
    if (index5 !== -1) {
      return 'Europe/Madrid';
    }

    let index6 = ['HST', 'HDT'].getElementIndex((x) => x === timezone.toUpperCase());
    if (index6 !== -1) {
      return 'Pacific/Honolulu';
    }
    return 'America/New_York';
  }

  private static showOnlyStandardTime(item) {
    if (item) {
      if (item.eventTimes && item.eventTimes.length) {
        item.eventTimes.forEach((x, index) => {
          if (x.startTimeFormatted) {
            x.startTimeFormatted = this.replaceDaytimeToStandardTimeOnlyForDisplaying(
              x.startTimeFormatted
            );
          }
          if (x.endTimeFormatted) {
            x.endTimeFormatted = this.replaceDaytimeToStandardTimeOnlyForDisplaying(
              x.endTimeFormatted
            );
          }
        });
      }
      if (item.startTimeFormatted) {
        item.startTimeFormatted = this.replaceDaytimeToStandardTimeOnlyForDisplaying(
          item.startTimeFormatted
        );
      }
      if (item.endTimeFormatted) {
        item.endTimeFormatted = this.replaceDaytimeToStandardTimeOnlyForDisplaying(
          item.endTimeFormatted
        );
      }
    }
  }

  private static replaceDaytimeToStandardTimeOnlyForDisplaying(time: string) {
    time = time.replace('EDT', 'EST');
    time = time.replace('CDT', 'CST');
    time = time.replace('PDT', 'PST');
    time = time.replace('MDT', 'MST');
    time = time.replace('AKDT', 'AKST');
    time = time.replace('CEST', 'CEDT');
    return time;
  }
}

export class PDFTimeBox {
  constructor(public month: string, public date: string, public day: string, public time: string) {}
}



// WEBPACK FOOTER //
// D:/Work/Development/vsts_build/_work/2/s/web/src/services/event.time.service.ts